import { cloneDeep, find, isNil, max, pick, pickBy } from 'lodash';

import 'url-search-params-polyfill';

import {
  TermInsuranceProduct,
  ProposalType,
  TERM_INSURANCE_PROVIDERS
} from '@common/types';
import {
  AcceptedDocument,
  CustomerRequirementStatus
} from '@store/customer-requirements/types';
import { HealthPolicyStatus } from '@store/health-status/types';
import { TermPolicyStatus } from '@store/term-status/types';
import { dayjs } from '@ui';
import { cannotEditHealthProposal } from '@views/health/status/utils';
import { isTermProposalSubmitted } from '@views/term/status/utils';

import {
  FATHER,
  KIDS,
  MOTHER,
  SELF,
  SPOUSE,
  TERM_INSURANCE_PRODUCTS,
  TERM_PRODUCTS_SLUGS,
  IPRU_PROPOSAL_SLUG,
  MAX_AND_HDFCLIFE_DOC_MAX_FILE_SIZE,
  DEFAULT_DOC_MAX_FILE_SIZE,
  UNDERSTAND_YOUR_HEALTH_POLICY_DMA_PATHS,
  UNDERSTAND_YOUR_HEALTH_POLICY_DMA_BASE_LINK,
  ONE_FEET_IN_CM,
  ONE_INCH_IN_CM,
  ONE_FEET_IN_INCHES,
  MAXLIFE_TERM_DOC_SUPPORTED_FILE_EXTS,
  TERM_DOC_SUPPORTED_FILE_EXTS,
  IPRU_DOC_MAX_FILE_SIZE,
  ADITYA_BIRLA_DOC_MAX_FILE_SIZE,
  HEALTH_INSURANCE_PRODUCTS
} from './constants';
import { captureException } from './sentry';

export const checkLoggedIn = (): boolean => {
  const loggedIn = localStorage.getItem('loggedIn');
  let isLoggedIn = false;
  if (loggedIn === null) {
    isLoggedIn = false;
  } else {
    isLoggedIn = JSON.parse(loggedIn);
  }

  return isLoggedIn;
};

export const convertToINR = (
  number: number | string,
  decimals?: number,
  expanded?: boolean, // THe abbreviated value will be expanded to Thousand, Lakh, Crore
  abbreviate = true // The value will be abbreviated to K, L, Cr
): string | number => {
  let value: number | string = parseInt(number.toString());

  const getLocaleValueString = (_abbreviatedValue: number) => {
    let abbreviatedValue = _abbreviatedValue;
    if (decimals) {
      abbreviatedValue = Number(abbreviatedValue.toFixed(decimals));
    } else {
      value = Math.round(abbreviatedValue);
    }

    return abbreviatedValue.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals ? decimals : 0,
      style: 'currency',
      currency: 'INR'
    });
  };

  if (value >= 10000000) {
    const isOneCrore = value === 10000000;

    if (abbreviate) {
      value = value / 10000000;
    }

    value = getLocaleValueString(value);
    if (abbreviate) {
      value =
        value +
        (!expanded ? '\u00a0Cr' : isOneCrore ? '\u00a0Crore' : '\u00a0Crores');
    }
  } else if (value >= 100000) {
    const isOneLakh = value === 100000;

    if (abbreviate) {
      value = value / 100000;
    }

    value = getLocaleValueString(value);
    if (abbreviate) {
      value =
        value + (!expanded ? '\u00a0L' : isOneLakh ? '\u00a0Lakh' : '\u00a0Lakhs');
    }
  } else if (value >= 1000) {
    if (abbreviate) {
      value = value / 1000;
    }

    value = getLocaleValueString(value);
    if (abbreviate) {
      value = value + (!expanded ? '\u00a0K' : '\u00a0Thousand');
    }
  } else {
    return getLocaleValueString(value);
  }

  return value;
};

export const isUnlimitedSI = (v: number | string | undefined): v is 'unlimited' =>
  v === 'unlimited';

export const formatSI = (
  v: number | string | undefined,
  decimals?: number,
  expanded?: boolean,
  abbreviate = true
): string => {
  if (v === undefined) return '-';
  if (isUnlimitedSI(v)) return 'Unlimited';
  return convertToINR(v, decimals, expanded, abbreviate) as string;
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  });
};

const isParentsSelected = (selectedList: Array<string>): boolean =>
  selectedList.includes(FATHER) || selectedList.includes(MOTHER);

export const generateParentsLabel = (selectedList: Array<string>): string => {
  const fatherIncluded = selectedList.includes(FATHER);
  const motherIncluded = selectedList.includes(MOTHER);

  if (fatherIncluded && motherIncluded) {
    return 'parents';
  } else if (fatherIncluded && !motherIncluded) {
    return 'father';
  } else if (!fatherIncluded && motherIncluded) {
    return 'mother';
  } else {
    return '';
  }
};

const isFamilySelected = (selectedList: Array<string>): boolean =>
  (selectedList.includes(SPOUSE) && selectedList.includes(SELF)) ||
  selectedList.includes(SPOUSE) ||
  (selectedList.includes(SELF) && selectedList.includes(KIDS)) ||
  selectedList.includes(SELF);

export const generateFamilyLabel = (selectedList: Array<string>): string => {
  const selfIncluded = selectedList.includes(SELF);
  const spouseIncluded = selectedList.includes(SPOUSE);
  const kidsIncluded = selectedList.includes(KIDS);
  if ((selfIncluded || spouseIncluded) && kidsIncluded) {
    return 'your family';
  } else if (selfIncluded && !spouseIncluded) {
    return 'you';
  } else if (!selfIncluded && spouseIncluded) {
    return 'your spouse';
  } else if (selfIncluded && spouseIncluded) {
    return 'you & your spouse';
  } else {
    return 'your family';
  }
};

export const getMembersLabel = (selectedList: Array<string>) => {
  if (isFamilySelected(selectedList)) {
    return generateFamilyLabel(selectedList);
  } else if (isParentsSelected(selectedList)) {
    return generateParentsLabel(selectedList);
  }
};

export const deepCopyObject = (object: any) => cloneDeep(object);

// TODO - Simplyfy this.
// Working solution added
export const isAgeChanged = (
  oldMemData: Array<any>,
  newMemData: Array<any>,
  checkForAnyMem = false,
  isCare = false,
  policyExpiryDate = null
) => {
  let currentDate = dayjs();
  if (isCare) {
    //For porting policy expiry date is considered as login date if the expiry date is after current date
    if (policyExpiryDate && dayjs(policyExpiryDate).isAfter(dayjs())) {
      currentDate = dayjs(policyExpiryDate);
    } else {
      // Care consider nextDay as login date
      currentDate = currentDate.add(1, 'days');
    }
  }

  const prevEldestPerson = oldMemData.reduce((member1, member2) => {
    if (member1.age < member2.age) {
      return member2;
    } else {
      return member1;
    }
  });

  // find how many members age changed.
  const ageChangedMembers: string[] = oldMemData.reduce(
    (acc, { member_id, age }) => {
      const member = find(newMemData, { member_id });
      const newAge = currentDate.diff(member.dob, 'years');
      if (age !== newAge) {
        acc.push(member.relation);
        return acc;
      } else {
        return acc;
      }
    },
    []
  );

  const ageChangeMemCount = ageChangedMembers.length;

  //find the eldest person in members new data
  const eldestPerson = newMemData.reduce((member1, member2) => {
    const mem1Age = currentDate.diff(member1.dob, 'years');
    const mem2Age = currentDate.diff(member2.dob, 'years');
    if (mem1Age <= mem2Age) {
      return { ...member2, age: mem2Age };
    } else {
      return { ...member1, age: mem1Age };
    }
  });

  // find the relation for change in age
  const relationWhosAgeChanged = () => {
    if (checkForAnyMem) {
      // When multiple people age changes return ''
      if (ageChangeMemCount > 1) return '';
      // Only one member age changes, return the relation
      return ageChangedMembers[0];
    }

    if (eldestPerson.relation === prevEldestPerson.relation) {
      return eldestPerson.relation;
    } else {
      const eldestPersonCurrentAge = eldestPerson.age;
      const eldestPersonPrevAge = find(oldMemData, {
        member_id: eldestPerson.member_id
      })?.age;

      if (eldestPersonCurrentAge !== eldestPersonPrevAge) {
        return eldestPerson.relation;
      } else {
        return prevEldestPerson.relation;
      }
    }
  };

  return {
    //if new eldest member age not same as previous
    answer: checkForAnyMem
      ? ageChangeMemCount > 0
      : currentDate.diff(eldestPerson.dob, 'years') !== prevEldestPerson.age,
    members: ageChangeMemCount,
    relationWhosAgeChanged: relationWhosAgeChanged()
  };
};

const {
  SMART_SECURE_PLUS: MLI_SMART_SECURE_PLUS_SLUG,
  CLICK_2_PROTECT_LIFE: C2PL_SLUG,
  CLICK_2_PROTECT_SUPER: C2PS_SLUG
} = TERM_PRODUCTS_SLUGS;

/**
 * check if current url passed is a status page/proposal page url
 *
 * @param url
 * @returns the app name to use to verify the user
 */
export const statusPageUrl = (url: string) => {
  let app = undefined;
  if (
    url.indexOf('health/') > -1 ||
    url.indexOf('niva_bupa/') > -1 ||
    url.indexOf('care-health/') > -1 ||
    url.indexOf('hdfc_ergo/') > -1
  ) {
    app = 'health';
  } else if (
    url.indexOf('life/') > -1 ||
    url.indexOf('term/') > -1 ||
    url.indexOf(`${C2PL_SLUG}/`) > -1 ||
    url.indexOf(`${C2PS_SLUG}/`) > -1 ||
    url.indexOf(`${IPRU_PROPOSAL_SLUG}/`) > -1 ||
    url.indexOf(`${MLI_SMART_SECURE_PLUS_SLUG}/`) > -1
  ) {
    app = 'life';
  }

  return app;
};

// as of now login and account are only customer account related
export const customerAccountUrl = (url: string) => {
  if (url.includes('/login') || url.includes('/account')) {
    return true;
  }
  return false;
};

export const getCoverUnavailableMessage = (
  amounts: number[],
  limitedSIReason: 'age' | 'deductible' | 'location' | null
): string => {
  const reasonMapping = {
    age: 'given age',
    deductible: 'selected deductible amount',
    location: 'entered pincode area'
  };

  if (amounts.length > 2 && limitedSIReason)
    return `A few cover amounts are unavailable for the ${reasonMapping[limitedSIReason]}`;

  const unAvailableMessage = amounts
    .reduce((amountInWords: Array<string | number>, amount: number) => {
      amountInWords.push(convertToINR(amount, 2, true));
      return amountInWords;
    }, [])
    .join(' & ');

  if (limitedSIReason)
    return `${unAvailableMessage} cover is not available for the ${reasonMapping[limitedSIReason]}`;
  return `${unAvailableMessage} cover is not available`;
};

export const getMaxAgeFromSelectedMembers = (selectedMembers: any, values: any) => {
  if (isParentsSelected(selectedMembers)) {
    const memberAges = [];
    const { fathersAge, mothersAge } = values;
    if (selectedMembers.includes(FATHER)) memberAges.push(fathersAge);
    if (selectedMembers.includes(MOTHER)) memberAges.push(mothersAge);
    return max(memberAges);
  }

  if (isFamilySelected(selectedMembers)) {
    const memberAges = [];
    const { age, spousesAge } = values;
    if (selectedMembers.includes(SELF)) memberAges.push(age);
    if (selectedMembers.includes(SPOUSE)) memberAges.push(spousesAge);
    // ignoring the kids ages hers as kids age never be more than than self or spouse.
    return max(memberAges);
  }
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes !== 0 && !!bytes == false) return 'Size Unavailable';
  if (bytes === 0) return '0 Bytes';

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const getProposalType = (pathname: string) => {
  if (pathname.includes('/term/')) {
    return ProposalType.term;
  }

  if (pathname.includes('/health/')) {
    return ProposalType.health;
  }

  return null;
};

// Ft and Inches to CM and viceversa convertors
// A foot contains 12 inches.
// 1 inch = 2.54 centimeters.
// 1 foot = 12 * 2.54 centimeters (or 30.48 centimeters).

export const convertCmToFeetAndInches = (centimeters: number) => {
  const totalInches = centimeters / ONE_INCH_IN_CM;
  const feet = Math.floor(totalInches / ONE_FEET_IN_INCHES);
  const inches = Math.round(totalInches % ONE_FEET_IN_INCHES);
  return { feet, inches };
};

export const convertFeetAndInchesToCm = (feet: number, inches: number) => {
  const ftIntoCm = feet * ONE_FEET_IN_CM;
  const inchIntoCm = inches * ONE_INCH_IN_CM;
  const centimeters = ftIntoCm + inchIntoCm;
  return Math.round(centimeters);
};

export const navigateToStatus = (policyID: string) => {
  const proposalType = getProposalType(window.location.pathname);
  if (proposalType === ProposalType.term) {
    window.location.href = `/term/status/${policyID}`;
    return;
  } else if (proposalType === ProposalType.health) {
    window.location.href = `/health/status/${policyID}`;
    return;
  }

  window.location.href = `/fq`;
  captureException(new Error('Invalid Proposal Type'));
};

export const cleanData = (data: { [key: string]: any }) => {
  return Object.entries(data)
    .filter(([_key, value]) => value !== undefined && value !== 0 && value !== '')
    .reduce((obj: { [key: string]: any }, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});
};

// Take a string and return a string with only alphanumeric chars and hyphen(-) and underscore(_)
export const cleanFileName = (input: string) => {
  return input.replace(/[^a-zA-Z0-9-_]/g, '');
};

// Get commas in a Indian format number
// 7 digits - 2 commas (99,99,999)
export const getFormattedNumberCommaCount = (digits: number) => {
  return digits <= 3 ? 0 : 1 + Math.floor((digits - 4) / 2);
};

// Get max length of a Indian format number inclusding commas (max digits + commas)
export const getFormattedNumberMaxLength = (maxLength: number) => {
  return maxLength + getFormattedNumberCommaCount(maxLength); // max length + commas
};

export const getSelectOptionsForAcceptedDocuments = (
  options: AcceptedDocument[]
) => {
  return options.map((option) => {
    const optionData = pickBy(
      pick(option, [
        'sample_file_path',
        'caution',
        'is_questionnaire',
        'questionnaire_document_type_id'
      ])
    );

    return {
      ...optionData,
      label: option.name,
      value: option.name
    };
  });
};

export const isCustomerRequirementCompleted = (
  status: CustomerRequirementStatus
) => {
  return [
    CustomerRequirementStatus.Accepted,
    CustomerRequirementStatus.WaivedOff
  ].includes(status);
};

// Allow Doc Upload only after medicals (hdfc,balic,maxlife,ipru)
export const isDocumentUploadInProgress = (
  status: TermPolicyStatus,
  provider: TERM_INSURANCE_PROVIDERS,
  isNRI = false
) => {
  switch (provider) {
    // For HDFC Life, NRI policies skip the medicals step, so payment done is the stage
    // where an NRI user can start uploading docs.
    case TERM_INSURANCE_PROVIDERS.HDFCLIFE:
      return isNRI
        ? status === TermPolicyStatus.PAYMENT_DONE
        : status === TermPolicyStatus.MEDICALS_SCHEDULED;
    // For Bajaj Life and ICICI Pru, medicals scheduled is the stage where user can start uploading docs.
    case TERM_INSURANCE_PROVIDERS.BALIC:
    case TERM_INSURANCE_PROVIDERS.ICICIPRU:
      return status === TermPolicyStatus.MEDICALS_SCHEDULED;
    // For Maxlife, medicals api could fail and status would remain in final push submitted or medicals initiated (set after payment done)
    // So can allow doc upload on Medicals Initiated or Scheduled or Final Push Submitted as this is not a blocker for doc submission
    case TERM_INSURANCE_PROVIDERS.MAXLIFE:
      return [
        TermPolicyStatus.FINAL_PUSH_SUBMITTED,
        TermPolicyStatus.MEDICALS_SCHEDULED,
        TermPolicyStatus.MEDICALS_INITIATED
      ].includes(status);
    default:
      return false;
  }
};

export const getMaxUploadableFileSizeByProduct = (product: string) => {
  const isAdityaBirla = [
    HEALTH_INSURANCE_PRODUCTS.ACTIV_ONE,
    HEALTH_INSURANCE_PRODUCTS.ACTIV_ONE_VYTL,
    HEALTH_INSURANCE_PRODUCTS.ACTIV_FIT
  ].includes(product);
  if (isAdityaBirla) {
    return ADITYA_BIRLA_DOC_MAX_FILE_SIZE;
  }

  const isMaxOrHdfcLife = [
    TERM_INSURANCE_PRODUCTS.SMART_SECURE_PLUS,
    TERM_INSURANCE_PRODUCTS.SMART_TOTAL_ELITE,
    TERM_INSURANCE_PRODUCTS.SMART_TERM_PLAN_PLUS,
    TERM_INSURANCE_PRODUCTS.CLICK_2_PROTECT_LIFE,
    TERM_INSURANCE_PRODUCTS.CLICK_2_PROTECT_SUPER,
    TERM_INSURANCE_PRODUCTS.CLICK_2_PROTECT_SUPREME,
    TERM_INSURANCE_PRODUCTS.CLICK_2_PROTECT_SUPREME_PLUS
  ].includes(product as TermInsuranceProduct);

  const isIpru = [
    TERM_INSURANCE_PRODUCTS.I_PROTECT_SMART_PLUS,
    TERM_INSURANCE_PRODUCTS.I_PROTECT_SMART
  ].includes(product as TermInsuranceProduct);

  const maxAllowedFileSize = isMaxOrHdfcLife
    ? MAX_AND_HDFCLIFE_DOC_MAX_FILE_SIZE
    : isIpru
    ? IPRU_DOC_MAX_FILE_SIZE
    : DEFAULT_DOC_MAX_FILE_SIZE;

  return maxAllowedFileSize;
};

export const getSupportedFileExtsByProduct = (product: TermInsuranceProduct) => {
  const isMaxLife = [
    TERM_INSURANCE_PRODUCTS.SMART_SECURE_PLUS,
    TERM_INSURANCE_PRODUCTS.SMART_TOTAL_ELITE,
    TERM_INSURANCE_PRODUCTS.SMART_TERM_PLAN_PLUS
  ].includes(product as TermInsuranceProduct);
  const supportedFileExts = isMaxLife
    ? MAXLIFE_TERM_DOC_SUPPORTED_FILE_EXTS
    : TERM_DOC_SUPPORTED_FILE_EXTS;

  return supportedFileExts;
};

export const isProposalSubmitted = function (
  status: HealthPolicyStatus | TermPolicyStatus
) {
  const proposalType = getProposalType(window.location.pathname);

  if (proposalType === ProposalType.term) {
    return isTermProposalSubmitted(status as TermPolicyStatus);
  }

  if (proposalType === ProposalType.health) {
    return cannotEditHealthProposal(status as HealthPolicyStatus);
  }
  return false;
};

/*
Converts "Lorem \\n Ipsum" to "Lorem \n Ipsum"
*/
export const normalizeEscapedLineBreak = (str: string) => {
  if (!str) return '';
  return str.replace(/\\n/g, '\n');
};

// Helper function to construct URL paths for DMA - Understand Your Health Policy Page
const constructUnderstandYourHealthPolicyPath = (
  product: string,
  planType?: string
) => {
  const productPath = UNDERSTAND_YOUR_HEALTH_POLICY_DMA_PATHS[product];

  // A product might not necessarily have plans
  if (typeof productPath === 'string') {
    return productPath;
  }

  //@ts-ignore
  if (planType && productPath && productPath[planType]) {
    //@ts-ignore
    return productPath[planType];
  }

  return null;
};

// Provide DMA - Understand Your Health Policy link for individual products (plan if available)
export const getUnderstandYourHealthPolicyLink = (
  product: string,
  planType?: string
): string => {
  const baseLink = UNDERSTAND_YOUR_HEALTH_POLICY_DMA_BASE_LINK;
  const productPath = constructUnderstandYourHealthPolicyPath(product, planType);

  // Fallback to base link which has insurer and product select on DMA
  return isNil(productPath) ? baseLink : `${baseLink}${productPath}`;
};

// Helper function for pluralization
export const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count !== 1 ? plural : singular}`;

/**
 * Strict date parser that only parses complete DD/MM/YYYY dates.
 * Returns invalid date for incomplete input to prevent partial year parsing (e.g., "10" becoming 2010).
 */
export const parseDateInput = (input: string): Date => {
  if (input.length === 10 && /^\d{2}\/\d{2}\/\d{4}$/.test(input)) {
    const [day, month, year] = input.split('/');
    return new Date(`${year}-${month}-${day}`);
  }
  return new Date(NaN);
};

// Prevent invalid characters in date input field and add '/' after DD and MM.
export const preventInvalidDateInput = (
  event: React.KeyboardEvent<HTMLInputElement>
) => {
  const target = event.target as HTMLInputElement;
  const currentValue = target.value;
  const key = event.key;

  // Allow navigation and editing keys
  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'];
  if (allowedKeys.includes(key)) return;

  // Only allow digits and forward slash
  if (!/[0-9/]/.test(key)) {
    event.preventDefault();
    return;
  }

  // Handle digit input with auto-formatting
  if (/[0-9]/.test(key)) {
    const length = currentValue.length;
    const slashCount = (currentValue.match(/\//g) || []).length;

    // Prevent entering more than 4 digits for year (DD/MM/YYYY format)
    // If we already have DD/MM/YYYY (10 characters), prevent further digit input
    if (length >= 10) {
      event.preventDefault();
      return;
    }

    // Auto-add "/" after DD (position 2) or MM (position 5)
    if ((length === 2 && slashCount === 0) || (length === 5 && slashCount === 1)) {
      event.preventDefault();
      target.value = currentValue + '/' + key;
      target.setSelectionRange(target.value.length, target.value.length);
    }
  }
};
